/**
 * WebSocket service hook for Aegix One.
 *
 * Single connection multiplexed across all real-time domains (currently
 * reunification). Matches AIM's connection lifecycle: ADR-049 exponential
 * backoff with jitter, graceful close codes for server drain/token refresh,
 * and auth-failure handling.
 *
 * The connect payload includes `supports_ack: true` so the backend can opt
 * the client into reliable broadcast.
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { redirectToLoginPreservingLocation } from '../logoutRedirect';
import { getOneAccessToken, oneStorage } from '../one-auth';
import {
  AUTH_FAILURE_CLOSE_CODES,
  GRACEFUL_CLOSE_CODES,
  GRACEFUL_RECONNECT_DELAY_MS,
  HARD_MAX_RETRIES,
  STABLE_CONNECTION_THRESHOLD_MS,
  getReconnectDelay,
  type ConnectionState,
} from './types';

/**
 * Read the JWT through ONE's auth seam. The `oneStorage` adapter owns
 * the raw-token / JSON-rest rule, the `__cookie__` scrub, the
 * JSON-quoted self-heal, and the `legacyKeys` promotion of
 * pre-canonical `aegix_one_*` slots — so the WS connect path inherits
 * the same single source of truth the http client and `<AuthProvider>`
 * read. An inline `localStorage.getItem(...)` here would trip the
 * `aegix/no-raw-token-storage-access` lint rule.
 */
function getToken(): string | null {
  return getOneAccessToken();
}

/**
 * Clear all auth-tier keys on a WS auth-failure
 * close. `oneStorage.clearAuthKeys()` drains both the canonical
 * `aegix.auth.v1.*` slots AND any still-present legacy
 * `aegix_one_*` entries declared via `legacyKeys` (so a returning
 * user that never triggered legacy promotion still gets a clean
 * slate at logout).
 */
function clearToken(): void {
  oneStorage.clearAuthKeys();
}

export type MessageListener = (message: string) => void;

export interface SocketService {
  addMessageListener: (listener: MessageListener) => void;
  removeMessageListener: (listener: MessageListener) => void;
  connectionState: ConnectionState;
}

export interface SocketServiceOptions {
  /**
   * Fires every time the socket transitions to `connected` — both the
   * initial open and every subsequent reconnect. Used by `SocketContext` to
   * fetch the recovery snapshot on every (re)connect.
   */
  onConnect?: (meta: { wasReconnect: boolean }) => void;
  /**
   * Fires only after a reconnect (retry count > 0). Kept as a focused
   * hook for reconnect-specific side-effects.
   */
  onReconnect?: () => void;
}

/**
 * Device identity: the socket's `device_id` is the
 * signed claim inside the JWT this URL carries — the claim the credential
 * MINT's stamped `x-device-id` established (see `lib/deviceIdHeader.ts`).
 * The connect deliberately does NOT restate a device id (no header, no
 * query param): the backend ignores a socket's own claim of identity,
 * which is what makes the binding unforgeable.
 */
function buildSocketUrl(token: string): string {
  const socketBase = window.__ENV?.NX_SOCKET_URL;
  if (socketBase) {
    return `${socketBase.replace(/\/+$/, '')}/api/v2/ws/?token=${encodeURIComponent(token)}`;
  }
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${window.location.host}/api/v2/ws/?token=${encodeURIComponent(token)}`;
}

export function useSocketService(
  isLoggedIn: boolean,
  options?: SocketServiceOptions,
): SocketService {
  const socketRef = useRef<WebSocket | null>(null);
  const listenersRef = useRef<MessageListener[]>([]);
  const reconnectTimeoutRef = useRef<number | null>(null);
  const stabilityTimerRef = useRef<number | null>(null);
  const retryCountRef = useRef<number>(0);
  const onReconnectRef = useRef<(() => void) | undefined>(options?.onReconnect);
  const onConnectRef = useRef<
    ((meta: { wasReconnect: boolean }) => void) | undefined
  >(options?.onConnect);

  const [connectionState, setConnectionState] = useState<ConnectionState>({
    status: 'idle',
    lastConnected: null,
    reconnectAttempt: 0,
  });

  // Keep the callbacks fresh without re-running the connection lifecycle
  // effect — the refs are read from inside the socket open/close handlers.
  useEffect(() => {
    onReconnectRef.current = options?.onReconnect;
    onConnectRef.current = options?.onConnect;
  }, [options?.onReconnect, options?.onConnect]);

  useEffect(() => {
    if (!isLoggedIn) {
      setConnectionState({
        status: 'idle',
        lastConnected: null,
        reconnectAttempt: 0,
      });
      return undefined;
    }

    let disposed = false;

    const clearTimers = () => {
      if (stabilityTimerRef.current !== null) {
        window.clearTimeout(stabilityTimerRef.current);
        stabilityTimerRef.current = null;
      }
    };

    const clearReconnectTimer = () => {
      if (reconnectTimeoutRef.current !== null) {
        window.clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
    };

    const scheduleReconnect = () => {
      if (disposed) return;
      if (reconnectTimeoutRef.current !== null) return;

      if (retryCountRef.current >= HARD_MAX_RETRIES) {
        setConnectionState((prev) => ({ ...prev, status: 'disconnected' }));
        return;
      }

      const delay = getReconnectDelay(retryCountRef.current);
      setConnectionState((prev) => ({
        ...prev,
        status: 'reconnecting',
        reconnectAttempt: retryCountRef.current + 1,
      }));

      reconnectTimeoutRef.current = window.setTimeout(() => {
        reconnectTimeoutRef.current = null;
        retryCountRef.current += 1;
        connect();
      }, delay);
    };

    const connect = () => {
      if (disposed) return;
      const token = getToken();
      if (!token) {
        setConnectionState((prev) => ({ ...prev, status: 'auth_failed' }));
        return;
      }

      clearReconnectTimer();

      let socket: WebSocket;
      try {
        socket = new WebSocket(buildSocketUrl(token));
      } catch {
        scheduleReconnect();
        return;
      }

      socket.onclose = (event: CloseEvent) => {
        clearTimers();
        if (disposed) return;

        if (AUTH_FAILURE_CLOSE_CODES.has(event.code)) {
          setConnectionState((prev) => ({
            ...prev,
            status: 'auth_failed',
            reconnectAttempt: 0,
          }));
          clearToken();
          // This kick is involuntary — remember the user's
          // place before the full-page bounce to /login (which never
          // mounts the router-level capture, `RedirectToLogin`), so the post-login
          // redirect can restore it.
          redirectToLoginPreservingLocation();
          return;
        }

        if (GRACEFUL_CLOSE_CODES.has(event.code)) {
          retryCountRef.current = 0;
          setConnectionState((prev) => ({
            ...prev,
            status: 'connecting',
            reconnectAttempt: 0,
          }));
          reconnectTimeoutRef.current = window.setTimeout(() => {
            reconnectTimeoutRef.current = null;
            connect();
          }, GRACEFUL_RECONNECT_DELAY_MS);
          return;
        }

        if (event.code === 1000) {
          // Normal close on logout/unmount — do not reconnect.
          return;
        }

        scheduleReconnect();
      };

      socket.onopen = () => {
        if (disposed) {
          try {
            socket.close(1000);
          } catch {
            // ignore
          }
          return;
        }
        socketRef.current = socket;
        const wasReconnect = retryCountRef.current > 0;

        if (stabilityTimerRef.current !== null) {
          window.clearTimeout(stabilityTimerRef.current);
        }
        stabilityTimerRef.current = window.setTimeout(() => {
          stabilityTimerRef.current = null;
          retryCountRef.current = 0;
        }, STABLE_CONNECTION_THRESHOLD_MS);

        setConnectionState({
          status: 'connected',
          lastConnected: new Date(),
          reconnectAttempt: 0,
        });

        socket.send(
          JSON.stringify({
            type: 'capabilities',
            supports_ack: true,
            client: 'aegix-one-react',
          }),
        );

        onConnectRef.current?.({ wasReconnect });
        if (wasReconnect) {
          onReconnectRef.current?.();
        }
      };

      socket.onmessage = (event: MessageEvent) => {
        const raw = event.data as string;
        try {
          const parsed = JSON.parse(raw);
          if (parsed?.type === 'server_shutdown') return;
          if (parsed?.requires_ack && parsed?.id && socketRef.current) {
            socketRef.current.send(
              JSON.stringify({ type: 'ack', message_id: parsed.id }),
            );
          }
        } catch {
          // Not JSON — pass through to listeners unchanged.
        }
        listenersRef.current.forEach((listener) => listener(raw));
      };

      socket.onerror = () => {
        // `onclose` fires next with the definitive close code; handle there.
      };
    };

    setConnectionState((prev) => ({ ...prev, status: 'connecting' }));
    connect();

    return () => {
      disposed = true;
      clearTimers();
      clearReconnectTimer();
      retryCountRef.current = 0;
      if (socketRef.current) {
        const sock = socketRef.current;
        socketRef.current = null;
        sock.onclose = null;
        sock.onerror = null;
        sock.onmessage = null;
        sock.onopen = null;
        try {
          sock.close(1000, 'client disconnect');
        } catch {
          // ignore
        }
      }
      setConnectionState((prev) => ({ ...prev, status: 'disconnected' }));
    };
  }, [isLoggedIn]);

  const addMessageListener = useCallback((listener: MessageListener) => {
    listenersRef.current.push(listener);
  }, []);

  const removeMessageListener = useCallback((listener: MessageListener) => {
    const index = listenersRef.current.indexOf(listener);
    if (index !== -1) listenersRef.current.splice(index, 1);
  }, []);

  return {
    addMessageListener,
    removeMessageListener,
    connectionState,
  };
}
